import { useEffect, useRef, useState } from 'react';

// PrimeReact components
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';

// Column definitions (already declared in appconfig)
import { AdmittedListColumns } from '../../appconfig/DatatableSetting';

// Data table settings
import { defaultRowOptions, defaultPageRowOptions, paginatorLinks, showingEntries, Skeletonitems, SkeletonbodyTemplate, filterDegreeList } from '../../appconfig/Settings';

// Services
import { getAllDetails } from '../../service/ProductService';

// Breadcrumb
import AppBreadcrumb from '../../components/CustomeBreadcrumb/AppBreadcrumb';

// API constants
import { GET_ADMITTED_LIST, GET_COLLEGES } from '../../config/ApiConstant';
import { CollegeManageTab } from './CollegeManageTab';
import { useNavigate } from 'react-router-dom';

export const AdmittedList = () => {
    document.title = 'Admitted List | GSPC';

    // Navigate
    const navigate = useNavigate();

    // Toast & filters
    const toast = useRef<Toast | null>(null);
    const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
    const [globalFilter, setGlobalFilter] = useState<string | null>(null);

    // Loading/data states
    const [loading, setLoading] = useState(false);
    const [admittedList, setAdmittedList] = useState<any[]>([]);
    const [colleges, setColleges] = useState<any[]>([]);
    const [selectedCollege, setSelectedCollege] = useState<any>(null);
    const [degreeValue, setDegreeValue] = useState<any>(null);

    // useefect
    useEffect(() => {
        getColleges();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // Fetch all colleges for dropdown
    const getColleges = async () => {
        try {
            const response = await getAllDetails(`${GET_COLLEGES}?type=all`);
            if (response) {
                const collegeData = response.map((c: any) => ({
                    name: c.orgnameeng,
                    code: c.orgcode
                }));
                setColleges(collegeData);
            } else {
                setColleges([]);
            }
        } catch {
            setColleges([]);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Could not load colleges'
            });
        }
    };

    // Fetch admitted list (optionally by college)
    const getAdmittedList = async (collegeId?: string) => {
        try {
            setLoading(true);
            const params: any = {};
            if (collegeId) {
                params.college_id = collegeId;
            }
            params.type = 'Admin'
            const url = `${GET_ADMITTED_LIST}?${new URLSearchParams(params)}`;
            const response = await getAllDetails(url);
            setAdmittedList(response || []);
        } catch {
            setAdmittedList([]);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Sorry, could not load data'
            });
        } finally {
            setLoading(false);
        }
    };

    // Reset filters & data
    const clearAll = () => {
        setSelectedCollege(null);
        setAdmittedList([]);
        setDegreeValue(null);
        setGlobalFieldFilter(null);
        setGlobalFilter(null);
    };

    const onCollegeChange = (e: { value: any }) => {
        setSelectedCollege(e.value);
        if (e.value) {
            getAdmittedList(e.value.code);
        } else {
            setAdmittedList([]);
        }
    };

    // left toolbar
    const leftToolbarTemplate = () => (
        <span className="search-bar mr-3">
            <i className="pi pi-search"></i>
            <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
        </span>
    );

    // right toolbar
    const rightToolbarTemplate = () => (
        <div className="flex gap-2">
            <Dropdown value={selectedCollege} options={colleges} optionLabel="name" placeholder="Select College" onChange={onCollegeChange} className="mr-2 w-25rem" filter filterBy="name" filterPlaceholder="Search college..." showClear />
            <Button icon="pi pi-filter-slash" label="Clear" onClick={clearAll} className="p-button-outlined p-button-primary mr-2" />
            <Button label="Pending Admitted List" onClick={() => navigate('/app/pending-admitted-list')} className="p-button-warning mr-2" />
        </div>
    );

    // Template for actions
    const actionsTemplate = (rowData: any) => {
        return (
            <div className="actions action-btn flex gap-2">
                {/* View Detail */}
                <Button tooltip="View Detail" tooltipOptions={{ position: 'top' }} icon="pi pi-eye" onClick={() => navigate(`/app/view-admitted-details/${window.btoa(rowData.id)}`)} />

                {/* Open File (only if admitted_list exists) */}
                {rowData.admitted_list && <Button tooltip="Open File" tooltipOptions={{ position: 'top' }} icon="pi pi-file" className="p-button-primary" onClick={() => window.open(rowData.admitted_list, '_blank')} />}
            </div>
        );
    };

    //  filter by degree template
    const filterByDegree = (options: any) => {
        return (
            <Dropdown
                filter
                value={degreeValue}
                options={filterDegreeList}
                onChange={(e) => {
                    setDegreeValue(e.value);
                    options.filterCallback(e.value ? e.value.name : null, options.index);
                }}
                placeholder="Filter by status"
                className="p-column-filter"
                optionLabel="name"
                showClear
            />
        );
    };

    return (
        <>
            <CollegeManageTab mainTabLabel="Admitted List" />
            <Toast ref={toast} />
            <AppBreadcrumb currentMenu="Admitted List" pageName="Approved Admitted List" pageTitle="Approved Admitted List" />
            <div className="grid crud-demo">
                <div className="col-12">
                    <div className="card">
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} />

                        {!loading ? (
                            <DataTable
                                value={admittedList}
                                showGridlines
                                stripedRows
                                globalFilter={globalFilter}
                                filters={globalFieldFilter}
                                onFilter={(e) => setGlobalFieldFilter(e.filters)}
                                rows={defaultRowOptions}
                                paginator={admittedList.length >= 10}
                                rowsPerPageOptions={defaultPageRowOptions}
                                className="datatable-responsive p-datatable-sm"
                                paginatorTemplate={paginatorLinks}
                                currentPageReportTemplate={showingEntries}
                                emptyMessage={selectedCollege ? 'No data available for the selected college' : 'No Data available'}
                                removableSort
                            >
                                {AdmittedListColumns.map((col, i) => {
                                    if (['from_year', 'to_year'].includes(col.field)) {
                                        return <Column key={col.field} field={col.field} header={col.header} filter filterPlaceholder={`Search by ${col.header}`} sortable />;
                                    } else if (col.field === 'sr_no') {
                                        return <Column key={col.field} field={col.field} header={col.header} body={(_: any, { rowIndex }) => rowIndex + 1} />;
                                    } else if (col.field === 'degree_name') {
                                        return <Column key={col.field} field={col.field} header={col.header} filter filterElement={filterByDegree} />;
                                    } else if (col.field === 'action') {
                                        return <Column key={col.field} header={col.header} body={actionsTemplate} />;
                                    } else {
                                        return <Column key={col.field} field={col.field} header={col.header} sortable />;
                                    }
                                })}
                            </DataTable>
                        ) : (
                            <DataTable value={Skeletonitems}>
                                {AdmittedListColumns.map((col) => (
                                    <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                ))}
                            </DataTable>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
